import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import CreationModalContainer from '@app/src/components/CreationModal/CreationModalContainer'
import CreationModalStep from '@app/src/components/CreationModal/CreationModalStep'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import type RequestError from '@app/src/errors/RequestError'
import useErrorNotification from '@app/src/hooks/errorNotification'
import AnswerFlagSegment from '@app/src/pages/Configurations/ConfigurationsPages/AnswerFlag/CreationSteps/AnswerFlagSegment'
import AnswerFlagSeverity from '@app/src/pages/Configurations/ConfigurationsPages/AnswerFlag/CreationSteps/AnswerFlagSeverity'
import AnswerFlagSummary from '@app/src/pages/Configurations/ConfigurationsPages/AnswerFlag/CreationSteps/AnswerFlagSummary'
import AnswerFlagTitle from '@app/src/pages/Configurations/ConfigurationsPages/AnswerFlag/CreationSteps/AnswerFlagTitle'
import AnswerFlagTrigger from '@app/src/pages/Configurations/ConfigurationsPages/AnswerFlag/CreationSteps/AnswerFlagTrigger'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { AnswerFlagRule } from '@app/src/types/answerFlags'
import type { Question, RiskStatus } from '@app/src/types/resourceExplorer'
import { AnswerRuleType } from '@app/src/types/resourceExplorer'
import { AmplitudeTrackingEvents, NotificationSeverity } from '@app/src/wf-constants'
import { Dialog } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import React, { useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

interface CreateAnswerFlagModalProps {
  onClose: () => void
  open: boolean
  editedRule?: AnswerFlagRule
}

export enum ANSWER_FLAG_FORM_KEYS {
  QUESTION_ID = 'questionId',
  QUESTION = 'question',
  RULE_TYPE = 'ruleType',
  RULE_VALUE = 'ruleValue',
  PROVIDER_FILTERS = 'providerFilters',
  RISK_STATUS = 'riskStatus',
  DESCRIPTION = 'description',
}

export interface AnswerFlagFormData {
  questionId?: number
  question?: Question
  ruleType?: AnswerRuleType
  ruleValue?: string | string[]
  providerFilters?: FilterGroup[]
  riskStatus?: RiskStatus
  description?: string
}

interface AnswerFlagQuestion {
  title: string
  description?: ReactNode
  fieldName: ANSWER_FLAG_FORM_KEYS | ''
  children: ReactNode
  overrideButtonTextId?: string
  hideNextButton?: boolean
  hasSkipButton?: boolean
  disableInitialBack?: boolean
  canGoNext?: (values: AnswerFlagFormData) => boolean
}

interface AnswerFlagRuleSaveModel {
  id?: number
  questionId: number
  description?: string
  riskStatus: RiskStatus
  providerFilters?: FilterGroup[]
  conditions: Array<{
    ruleType: AnswerRuleType
    ruleValue: string
  }>
}

const mapRuleToFormData = (rule: AnswerFlagRule): AnswerFlagFormData => {
  const firstCondition = rule.conditions?.[0]
  const ruleValues = rule.conditions?.map(condition => condition.ruleValue) ?? []

  // AnswerStatus and SelectedValue use multi-select, so always return an array
  const shouldBeArray =
    firstCondition?.ruleType === AnswerRuleType.AnswerStatus ||
    firstCondition?.ruleType === AnswerRuleType.SelectedValue

  return {
    questionId: rule.questionId,
    question: rule.question,
    ruleType: firstCondition?.ruleType,
    ruleValue: shouldBeArray ? ruleValues : ruleValues[0],
    providerFilters: rule.providerFilters ?? [],
    riskStatus: rule.riskStatus,
    description: rule.description,
  }
}

function normalizeRuleValues(ruleValue: string | string[] | undefined): string[] {
  if (ruleValue === undefined) return []
  if (Array.isArray(ruleValue)) return ruleValue
  return [ruleValue]
}

function mapFormDataToSaveModel(formData: AnswerFlagFormData, existingId?: number): AnswerFlagRuleSaveModel {
  const { questionId, riskStatus, ruleType } = formData

  if (questionId == null) {
    throw new Error('AnswerFlagFormData.questionId is required to create a save model.')
  }
  if (riskStatus == null) {
    throw new Error('AnswerFlagFormData.riskStatus is required to create a save model.')
  }
  if (ruleType == null) {
    throw new Error('AnswerFlagFormData.ruleType is required to create a save model.')
  }

  const ruleValues = normalizeRuleValues(formData.ruleValue)

  if (ruleValues.length === 0) {
    throw new Error('At least one rule value is required.')
  }

  // Deduplicate rule values to prevent duplicate rules in the database
  const uniqueRuleValues = [...new Set(ruleValues)]

  return {
    id: existingId,
    questionId,
    description: formData.description,
    riskStatus,
    providerFilters: formData.providerFilters ?? [],
    conditions: uniqueRuleValues.map(value => ({
      ruleType,
      ruleValue: value,
    })),
  }
}

const CreateAnswerFlagModal: React.FC<CreateAnswerFlagModalProps> = ({ onClose, open, editedRule }) => {
  const { activeStep, setActiveStep, setTotalSteps } = useCreationModalProgress()
  const { formatMessage, locale } = useIntl()
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const { trackEvent } = useAmplitude()
  const isEditMode = Boolean(editedRule)

  const trackChange = (rule: AnswerFlagRule | undefined = editedRule) => {
    trackEvent({
      name: AmplitudeTrackingEvents.Analyze.SupplierRisk.ConfiguredFlags,
      eventProps: {
        action: isEditMode ? 'edit' : 'create',
        ruleId: rule?.id,
        ruleTitle: rule?.description,
      },
    })
  }

  const { mutateAsync: saveRule } = useCreateResource<AnswerFlagRule, AnswerFlagRuleSaveModel>({
    options: {
      onSuccess: async result => {
        await queryClient.invalidateQueries({ queryKey: [FetchKey.AnswerFlagRuleCollection] })
        trackChange(result)
        showSnackbar({
          severity: NotificationSeverity.success,
          message: formatMessage({
            id: isEditMode ? 'answerFlagRules.notifications.updated' : 'answerFlagRules.notifications.created',
          }),
        })
        onClose()
      },
      onError: (error: RequestError) => {
        showErrorNotification({ requestError: error })
      },
    },
  })

  const formMethods = useForm<AnswerFlagFormData>({
    shouldUnregister: false,
    defaultValues: {
      [ANSWER_FLAG_FORM_KEYS.PROVIDER_FILTERS]: [],
    },
  })

  const { handleSubmit, reset } = formMethods

  useEffect(() => {
    if (!open) {
      setActiveStep(1)
      reset({ [ANSWER_FLAG_FORM_KEYS.PROVIDER_FILTERS]: [] })
    }
  }, [open, setActiveStep, reset])

  useEffect(() => {
    if (editedRule) {
      const formData = mapRuleToFormData(editedRule)
      reset(formData)
    }
  }, [editedRule, reset])

  const QUESTIONS: AnswerFlagQuestion[] = [
    {
      title: formatMessage({ id: 'answerFlagRules.creation.trigger.title' }),
      description: formatMessage({ id: 'answerFlagRules.creation.trigger.description' }),
      fieldName: ANSWER_FLAG_FORM_KEYS.RULE_VALUE,
      children: <AnswerFlagTrigger />,
      canGoNext: values =>
        Boolean(values.questionId) && Boolean(values.ruleType) && (values.ruleValue?.length ?? 0) > 0,
    },
    {
      title: formatMessage({ id: 'answerFlagRules.creation.segment.title' }),
      description: formatMessage({ id: 'answerFlagRules.creation.segment.description' }),
      fieldName: ANSWER_FLAG_FORM_KEYS.PROVIDER_FILTERS,
      children: <AnswerFlagSegment />,
      hasSkipButton: true,
    },
    {
      title: formatMessage({ id: 'answerFlagRules.creation.severity.title' }),
      description: formatMessage({ id: 'answerFlagRules.creation.severity.description' }),
      fieldName: ANSWER_FLAG_FORM_KEYS.RISK_STATUS,
      children: <AnswerFlagSeverity />,
    },
    {
      title: formatMessage({ id: 'answerFlagRules.creation.answerFlagTitle.title' }),
      description: formatMessage({ id: 'answerFlagRules.creation.answerFlagTitle.description' }),
      fieldName: ANSWER_FLAG_FORM_KEYS.DESCRIPTION,
      children: <AnswerFlagTitle />,
    },
    {
      title: formatMessage({ id: 'answerFlagRules.creation.summary.title' }),
      description: formatMessage({ id: 'answerFlagRules.creation.summary.description' }),
      fieldName: '',
      children: <AnswerFlagSummary />,
      overrideButtonTextId: 'answerFlagRules.creation.submit',
    },
  ]

  useEffect(() => {
    setTotalSteps(QUESTIONS.length)
  }, [QUESTIONS.length, setTotalSteps])

  const activeQuestion = useMemo(() => QUESTIONS[activeStep - 1], [activeStep, QUESTIONS.length, locale])

  const onSubmit = async (values: AnswerFlagFormData) => {
    const saveModel = mapFormDataToSaveModel(values, editedRule?.id)
    await saveRule({ url: endpoints.saveAnswerFlagRule, body: saveModel })
  }

  return (
    <Dialog open={open} fullScreen onClose={onClose}>
      <CreationModalContainer
        title={formatMessage({
          id: isEditMode ? 'answerFlagRules.update.modalTitle' : 'answerFlagRules.creation.modalTitle',
        })}
        onClose={() => {
          onClose()
          setActiveStep(1)
        }}
      >
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {activeQuestion && (
              <CreationModalStep
                title={activeQuestion.title}
                description={activeQuestion.description}
                fieldnames={[activeQuestion.fieldName].filter(Boolean)}
                onBack={() => {
                  if (activeStep === 1) {
                    onClose()
                  } else {
                    setActiveStep(activeStep - 1)
                  }
                }}
                hideNextButton={activeQuestion.hideNextButton}
                disableInitialBack={activeQuestion.disableInitialBack}
                hasSkipButton={activeQuestion.hasSkipButton}
                overrideButtonTextId={activeQuestion.overrideButtonTextId}
                canGoNext={
                  activeQuestion.canGoNext
                    ? (values: unknown) => {
                        const formData = values as AnswerFlagFormData
                        return activeQuestion.canGoNext?.(formData) ?? true
                      }
                    : undefined
                }
              >
                {activeQuestion.children}
              </CreationModalStep>
            )}
          </form>
        </FormProvider>
      </CreationModalContainer>
    </Dialog>
  )
}

export default CreateAnswerFlagModal
